import React, { useState, useEffect, useRef, useMemo, useCallback, useContext, useImperativeHandle } from 'react';
import { Box, ButtonBase, Grid, makeStyles, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import moment from 'moment';
import * as Yup from 'yup';
import _ from 'lodash';
import { bedStatusAPI, eadmissionAPI } from '../../../apis';
import { ColumnItem } from '../../../components';
import AutoCompleteMultiCheckbox from '../../../components/AutoCompleteMultiCheckbox';
import {
  DateTimeIcon,
  DocIcon,
  PendingFYAIcon,
  CloseIcon,
  OutstandingIcon,
  DropUpIcon,
  DropDownIcon,
  UpdateIcon,
  CompletedIcon,
  WithdrawIcon,
} from '../../../components/Icon';
import { i18n } from '../../../language';
import { classNames, downloadFileById, getUserInfo, isRequest } from '../../../utils';
import { checkSubmitData } from '../../../utils/check';
import { alertMsg, ResponseCommCodeEnum } from '../../../apis/commlogic';
import { LoginPop } from '../../../components/Login';
import exportPwdExcel from '../../../utils/exportPwdExcel';
import { getTransferInOutAdmissionTypeList, revampTableList } from '../../../utils/newBedStatusUtil';
import { ADMISSION_TRANSFER_IN_FILTER } from '../../../comm/constant/storage';
import { AdmissionContext } from '..';
import TransferInTable from './components/TransferInTable';
import TransferOutPop from './components/TransferOutPop';
import DateFilter from './components/DateFilter';
import {
  buildCreateTransferInParams,
  buildUpdateTransferInParams,
  fetchTransferInPopupOptions,
  initTransferInPopupOptions,
} from './transferInRequestUtil';
import TransferInPop from './components/TransferInPop';

const useStyles = makeStyles((theme) => ({
  root: ({ fullScreen }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '1rem',
    borderRadius: '0.9375rem',
    backgroundColor: '#FFF',
    height: '100%',
    overflow: 'auto hidden',
  }),
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.875rem',
    borderBottom: 'solid 1px #ddd',
  },
  bookColumn: {
    flexBasis: 'unset',
    padding: '1.844rem 1.875rem 1.875rem',
  },
  select: {
    width: '9.375rem',
    height: '2.5rem',
    opacity: 0.85,
    flexGrow: 0,
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '0.5rem',
      boxShadow: '-1px 4px 10px 0 rgba(221, 232, 251, 0.58)',
      border: 'solid 1px #ddd',
    },
    '& .MuiSelect-root': {
      lineHeight: '2.5rem',
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
  },
  filter: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2e4469',
    whiteSpace: 'nowrap',
  },
  ward: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e4469',
  },
  newBooking: {
    width: '10rem',
    height: '3rem',
    borderRadius: '0.625rem',
    background: '#538fff',
    fontSize: '1rem',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
    color: '#fff',
  },
  dateTimeIcon: {
    fontSize: '1.25rem',
    marginRight: '0.188rem',
  },
  columnItem: {
    width: '8.925rem',
    margin: '0 2rem',
    [theme.breakpoints.only('xs')]: {
      width: '100%',
    },
    marginLeft: '0.5rem',
    whiteSpace: 'nowrap',
  },
  columnBox: {
    display: 'flex',
    justifyContent: 'start',
    flexWrap: 'wrap',
    margin: '0 1.875rem 1rem 1.875rem',
    padding: '0.175rem 0rem 0.175rem 0.175rem',
    borderRadius: '0.937rem',
    border: '1.2px solid rgb(228, 231, 235)',
    alignItems: 'center',
  },
  filterBox: {
    // display: 'flex',
    // flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '0rem 0 1.5rem',
    borderBottom: 'solid 1px #ddd',
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  filterStatus: {},
  filterStatusTitle: {
    fontSize: '1rem',
    color: '#2e4469',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
    marginRight: '0.625rem',
  },
  detailBtn: ({ detailOpen }) => ({
    fontFamily: 'NotoSans-Medium',
    color: detailOpen ? '#7d86a9' : '#fff',
    fontSize: '1rem',
    width: '11.25rem',
    minWidth: '11.25rem',
    height: '2rem',
    position: 'absolute',
    top: '-1rem',
    left: '1.75rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '4px',
    zIndex: '99',
    backgroundColor: detailOpen ? '#fff' : '#7d86a9',
  }),
  dropIcon: {
    fontSize: '0.875rem',
    marginLeft: '1rem',
  },
  datepicker: {
    '& .MuiInputBase-root': {
      borderRadius: '0.5rem',
      backgroundColor: theme.palette.font.white,
      borderColor: '#e4e7eb',
      paddingLeft: '0.3rem',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '0.919rem',
      letterSpacing: '0',
      color: theme.palette.font.sub,
      fontWeight: '500',
      marginLeft: '-0.3rem',
      width: '96px',
      paddingRight: '0',
    },
  },
  disabledStyle: {
    '& .MuiInputBase-root': {
      backgroundColor: '#f5f5f5',
    },
  },
  flexSpace: {
    display: 'block',
    minWidth: 0,
    flex: 1,

    [`@media (max-width: 1690px)`]: {
      display: 'none',
    },
  },
  reset: {
    color: '#538FFF',
    fontSize: '1rem',
    textDecoration: 'underline',
    fontFamily: 'NotoSans-Bold',
  },
}));

const initTransferInFilter = () => {
  // Transfer In只缓存新增的两个下拉筛选条件。
  const storageFilter = JSON.parse(localStorage.getItem(ADMISSION_TRANSFER_IN_FILTER));
  return {
    specialtys: storageFilter?.specialtys ?? [],
    admissionTypes: storageFilter?.admissionTypes ?? [],
  };
};

const TransferIn = ({ contentHeight }) => {
  const { admissionParamsRef, tabValue, tabValues, needRefresh, eadViewVisible, specialty } =
    useContext(AdmissionContext);
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const [checkDataFlag, setCheckDataFlag] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const tabContentHeight = contentHeight > 0 ? `${contentHeight}px` : `calc(100vh - ${fullScreen ? 5 : 10}rem)`;
  const classes = useStyles({ detailOpen, fullScreen });
  const columnTitles = [
    // TO
    i18n.t('BEDSTATUS_PENDING'),
    i18n.t('BEDSTATUS_ACCEPTED'),
    i18n.t('BEDSTATUS_DECLINED'),

    // TI
    'Pending Accept',
    i18n.t('BEDSTATUS_OUTSTANDING'),
    i18n.t('BEDSTATUS_COMPLETED_SINCE_24HOURS_AGO'),
    'Withdrawn',
  ];
  const iconBgColors = [
    // TO
    '#ffc95c',
    '#5cd9f4',
    '#ff655c',
    // TI
    '#FA8A82',
    '#7FD6F1',
    '#28D2AB',
    '#878687',
  ];
  const tableRef = useRef(null);
  const transferInTabInitialRef = useRef(true);
  const PAGESIZE = 20;
  const language = useSelector((state) => state.settings.language);
  const allTransferInStatusList = useMemo(
    () => [
      // TO的状态
      { text: i18n.t('BEDSTATUS_PENDING'), value: 'P' },
      { text: i18n.t('BEDSTATUS_ACCEPTED'), value: 'A' },
      { text: i18n.t('BEDSTATUS_DECLINED'), value: 'DL' },
      { text: i18n.t('BEDSTATUS_COMPLETED_SINCE_24HOURS_AGO'), value: 'C' },
      { text: i18n.t('BEDSTATUS_DELETED'), value: 'D' },
      { text: i18n.t('BEDSTATUS_RECV_WARD_READY'), value: 'RWR' },
      { text: 'Draft', value: 'DRAFT' },

      // TI的状态
      { text: 'Pending Accept', value: 'PFYA' },
      // { text: i18n.t('BEDSTATUS_DECLINED'), value: 'DL' },
      { text: i18n.t('BEDSTATUS_OUTSTANDING'), value: 'O' },
      { text: i18n.t('BEDSTATUS_WARD_READY'), value: 'WR' },
      // { text: i18n.t('BEDSTATUS_COMPLETED_SINCE_24HOURS_AGO'), value: 'C' },
      // { text: 'Withdrawn', value: 'D' },
    ],
    [language]
  );
  const isTransferInCompleted = tabValue === tabValues.TransferInCompleted;
  const transferInStatusList = useMemo(
    () =>
      _.filter(allTransferInStatusList, (item) =>
        isTransferInCompleted
          ? ['C', 'D'].includes(item.value)
          : ['P', 'A', 'DL', 'RWR', 'DRAFT', 'PFYA', 'O', 'WR'].includes(item.value)
      ),
    [allTransferInStatusList, isTransferInCompleted]
  );
  const defaultTransferInStatusValues = useMemo(
    () => _.map(transferInStatusList, (item) => item.value),
    [transferInStatusList]
  );
  const [transferInStatus, setTransferInStatus] = useState([]);
  const [loginPopup, setLoginPopup] = useState(false);
  const userRole = useSelector((state) => state.userRole);
  const loginInfo = useSelector((state) => state.loginInfo);
  const rouList = useSelector((state) => state.rouList);
  const [editData, setEditData] = useState(null);
  const [transferOutPopOpen, setTransferOutPopOpen] = useState(false);
  const [transferInPopOpen, setTransferInPopOpen] = useState(false);
  const [startDate, setStartDate] = useState(() => moment());
  const [stopDate, setStopDate] = useState(() => moment());
  const [dateFilterResetKey, setDateFilterResetKey] = useState(0);
  const [transferInFilter, setTransferInFilter] = useState(initTransferInFilter);
  const { specialtys, admissionTypes } = transferInFilter;
  const [transferInList, setTransferInList] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [transferInPopupOptions, setTransferInPopupOptions] = useState(initTransferInPopupOptions);
  const schema = {
    startDate: stopDate
      ? Yup.date()
          .max(
            stopDate,
            i18n.t('ALERT_START_DATE_CANNOT_BE_LATER_THAN_END_DATE_CANNOT_BE_LATE_THAN_ENDDATE', {
              value: startDate?.format('DD-MM-yyyy'),
            })
          )
          .min(
            moment(stopDate).subtract(100, 'days'),
            i18n.t('BEDSTATUS_START_DATE_AND_END_DATE_CANNOT_BE_OVER_100_DAYS')
          )
          .typeError(i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') }))
      : Yup.date().typeError(i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') })),
    stopDate: Yup.date().typeError(
      i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_END_DATE') })
    ),
  };
  const [columnData, setColumnData] = useState([
    // TO的各种状态的数量
    { id: 0, text: 0, IconCom: PendingFYAIcon },
    { id: 1, text: 0, IconCom: OutstandingIcon },
    { id: 2, text: 0, IconCom: CloseIcon },

    // TI的各种状态的数量
    { id: 3, text: 0, IconCom: UpdateIcon },
    // { id: 4, text: 0, IconCom: CloseIcon, padding: '0.5rem' },
    { id: 5, text: 0, IconCom: OutstandingIcon },
    { id: 6, text: 0, IconCom: CompletedIcon },
    { id: 7, text: 0, IconCom: WithdrawIcon },
  ]);
  const admissionTypeList = useMemo(
    () => getTransferInOutAdmissionTypeList(transferInPopupOptions?.bsMlookupList),
    [transferInPopupOptions?.bsMlookupList]
  );
  const specialtyList = useMemo(() => {
    return _.uniqBy(
      _.concat(
        _.map(_.concat(specialty.specialtyCodes, specialty.subSpecialtyCodes), (item) => ({
          text: item,
          value: item,
        })),
        transferInFilter.specialtys
      ),
      'value'
    );
  }, [specialty, transferInFilter.specialtys]);

  const handleResetFilter = () => {
    const defaultDate = moment();
    setTransferInFilter({
      specialtys: [],
      admissionTypes: [],
    });
    setStartDate(defaultDate);
    setStopDate(defaultDate);
    setTransferInStatus([]);
    setDateFilterResetKey((value) => value + 1);
  };

  const closePop = (type) => {
    if (type === 'TI') {
      setTransferInPopOpen(false);
    } else {
      setTransferOutPopOpen(false);
    }
    setEditData(null);
  };
  const handleTOSubmit = async (data) => {
    closePop('TO');
    if (editData) {
      await handleEditTORequest(data);
      return;
    }
    await handleCreateTORequest(data);
  };
  const handleEditTIRequest = async (requestVal) => {
    try {
      await bedStatusAPI.postCreateBsBooking({ ...requestVal, wardId: loginInfo.wardId });
      closePop('TI');

      const currentIndex = _.findIndex(
        transferInList,
        (item) => item.transferRequestId === requestVal.transferRequestId
      );
      const pageNo = currentIndex > -1 ? Math.ceil((currentIndex + 1) / PAGESIZE) : 1;
      await fetchTransferInList(PAGESIZE, pageNo);
    } catch (error) {
      if (error.statusCode === ResponseCommCodeEnum.BED_VERSION) {
        alertMsg(
          i18n.t(
            'BEDSTATUS_THE_TRANSFER_IN_DETAIL_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_PLEAS_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_TRANSFER_IN_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
          ),
          _.uniqueId('warning_'),
          'warning'
        );
      }
    }
  };
  const handleClickNewRequest = () => {
    if (!loginInfo?.flag) {
      setLoginPopup(true);
    }
    setTransferOutPopOpen(true);
  };
  const setDate = (type) => {
    return (event) => {
      if (type === 'start') {
        setStartDate(event);
      } else if (type === 'stop') {
        setStopDate(event);
      }
    };
  };
  const getTransferInStatusParam = () => {
    const selectedStatus = transferInStatus
      .map((i) => i?.value)
      .filter((value) => defaultTransferInStatusValues.includes(value));
    return selectedStatus.length > 0 ? selectedStatus : defaultTransferInStatusValues;
  };

  const buildTransferInListParams = (pageSize, pageNo) => {
    const status = getTransferInStatusParam();
    return {
      endDate: stopDate ? stopDate.format('YYYY-MM-DD') : null,
      pageNo: pageNo,
      pageSize: pageSize,
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      // Admission Type filter使用选项value传给API。
      admissionTypes: _.map(admissionTypes, (item) => item.value),
      // Specialty filter使用选项value传给API。
      specialtys: _.map(specialtys, (item) => item.value),
      status,
    };
  };

  const updateTransferInStatistic = (result) => {
    setColumnData([
      // TO
      { ...columnData[0], text: result?.pendingCount ?? 0 },
      { ...columnData[1], text: result?.acceptedCount ?? 0 },
      { ...columnData[2], text: result?.declinedCount ?? 0 },

      // TI
      { ...columnData[3], text: result?.pendingFYACount ?? 0 },
      // { ...columnData[4], text: result?.declinedCount ?? 0 },
      { ...columnData[4], text: result?.outstandingCount ?? 0 },
      { ...columnData[5], text: result?.completedCount ?? 0 },
      { ...columnData[6], text: result?.withdrawnCount ?? 0 },
    ]);
  };

  const mergeTransferInList = async (result, pageSize, pageNo) => {
    // Decrypt HKIDs (revampTableList returns the resolved array)
    const decryptedData = await revampTableList(result.data);
    setTransferInList((preTransferInList) => [
      ...preTransferInList.slice(0, (pageNo - 1) * pageSize),
      ...decryptedData,
    ]);
    return decryptedData;
  };

  const fetchTransferInList = async (pageSize, pageNo) => {
    try {
      const { result } = await eadmissionAPI.getTransferInList(buildTransferInListParams(pageSize, pageNo));
      setTotalCount(result?.count ?? 0);
      updateTransferInStatistic(result);
      return mergeTransferInList(result, pageSize, pageNo);
    } catch (e) {}
  };

  // 页面级缓存弹窗的下拉和 lookup 数据，避免每次打开弹窗都重新请求。
  const loadTransferInPopupOptions = useCallback(async () => {
    const options = await fetchTransferInPopupOptions(loginInfo);
    setTransferInPopupOptions(options);
  }, [loginInfo.hospitalId, loginInfo.wardId]);

  // 提供给 Admission Header 的 refresh 按钮触发当前页面重新查询
  useImperativeHandle(admissionParamsRef, () => ({
    setRefresh,
  }));

  useEffect(() => {
    loadTransferInPopupOptions();
  }, [loadTransferInPopupOptions, refresh]);

  useEffect(() => {
    // 保存Transfer In新增的两个filter，刷新页面后可以回显。
    localStorage.setItem(ADMISSION_TRANSFER_IN_FILTER, JSON.stringify(transferInFilter));
  }, [transferInFilter]);

  // 切换Transfer In Pending/Completed时清空旧状态筛选。
  useEffect(() => {
    if (transferInTabInitialRef.current) {
      transferInTabInitialRef.current = false;
      return;
    }

    setTransferInStatus([]);
  }, [tabValue]);

  useEffect(() => {
    (async () => {
      if (checkDataFlag) {
        try {
          await checkSubmitData(Yup.object().shape(schema), { startDate, stopDate });
          setErrorMsg({ ...errorMsg, startDate: null, stopDate: null });
        } catch (e) {
          setErrorMsg({ ...errorMsg, ...e });
        }
      }
    })();
  }, [language]);

  useEffect(() => {
    (async () => {
      try {
        await checkSubmitData(Yup.object().shape(schema), { startDate, stopDate });
        await fetchTransferInList(PAGESIZE, 1);
        tableRef?.current?.scrollTo(0, 0);
        setErrorMsg({ ...errorMsg, startDate: null, stopDate: null });
      } catch (e) {
        setErrorMsg({ ...errorMsg, ...e });
      }
    })();
  }, [startDate, stopDate, tableRef, transferInStatus, admissionTypes, specialtys, needRefresh, rouList, refresh]);

  useEffect(() => {
    if (isRequest(loginInfo, userRole)) {
      setTotalCount(null);
      setTransferInList([]);
    }
  }, [rouList]);

  const handleCreateTORequest = async (requestVal) => {
    try {
      await eadmissionAPI.createTransferInRequest(buildCreateTransferInParams(requestVal, loginInfo.wardId));
      tableRef?.current?.scrollTo(0, 0);
      await fetchTransferInList(PAGESIZE, 1);
    } catch (error) {
      if (error.statusCode === ResponseCommCodeEnum.BED_VERSION) {
        alertMsg(
          i18n.t(
            'BEDSTATUS_THE_TRANSFER_IN_DETAIL_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_PLEAS_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_TRANSFER_IN_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
          ),
          _.uniqueId('warning_'),
          'warning'
        );
      }
    }
  };

  const handleEditTORequest = async (requestVal) => {
    try {
      await eadmissionAPI.updateTransferInRequest(buildUpdateTransferInParams(requestVal));
      const currentIndex = _.findIndex(
        transferInList,
        (item) => item.transferRequestId === requestVal.transferRequestId
      );
      const pageNo = currentIndex > -1 ? Math.ceil((currentIndex + 1) / PAGESIZE) : 1;
      await fetchTransferInList(PAGESIZE, pageNo);
      return true;
    } catch (error) {
      if (error.statusCode === ResponseCommCodeEnum.BED_VERSION) {
        alertMsg(
          i18n.t(
            'BEDSTATUS_THE_TRANSFER_IN_DETAIL_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_PLEAS_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_TRANSFER_IN_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
          ),
          _.uniqueId('warning_'),
          'warning'
        );
      }
    }
  };

  const exportExcelHandle = async (protectKey) => {
    try {
      const params = {
        wardId: loginInfo.wardId,
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        endDate: stopDate ? stopDate.format('YYYY-MM-DD') : null,
        status: getTransferInStatusParam(),
        admissionTypes: _.map(admissionTypes, (item) => item.value),
        specialtys: _.map(specialtys, (item) => item.value),
        protectKey,
        pageNo: 1,
        pageSize: totalCount ?? 0,
      };

      await downloadFileById(
        () => {
          return eadmissionAPI.getAdmissionTIExportRequest(params);
        },
        () => {
          return `Admission TransferIn Export Excel_${moment().valueOf()}`;
        }
      );
    } catch (error) {}
  };

  const getWardName = () => {
    const userInfo = getUserInfo();
    return userInfo?.wardName ?? '';
  };

  const handleOpenDetail = () => {
    setDetailOpen(!detailOpen);
  };

  const handleScroll = useCallback(
    ({ scrollTop, clientHeight, scrollHeight }) => {
      if (scrollTop + clientHeight + 1 >= scrollHeight) {
        if (transferInList.length < totalCount) {
          const pageNo = Math.ceil((transferInList.length + 1) / PAGESIZE);
          fetchTransferInList(PAGESIZE, pageNo);
        }
      }
    },
    [transferInList, totalCount]
  );

  const handleUpdateSingleData = (transferRequestId) => {
    const currentIndex = _.findIndex(transferInList, (item) => item.transferRequestId === transferRequestId);
    const currentPage = currentIndex > -1 ? Math.ceil((currentIndex + 1) / PAGESIZE) : 1;
    fetchTransferInList(PAGESIZE, currentPage);
  };

  // Page auto refresh (every 5 mins)
  useEffect(() => {
    const timer = setInterval(() => {
      (async () => {
        await fetchTransferInList(PAGESIZE, 1);
      })();
    }, 60 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [startDate, stopDate, transferInStatus, needRefresh]);

  return (
    <Box height={tabContentHeight} maxHeight={tabContentHeight} p="0.875rem 1.87rem 0">
      <div className={classes.root}>
        <div className={classes.filterBox}>
          <Box display="flex" alignItems="center" gridGap="0.6rem" flexWrap="wrap">
            <Box>
              <DateFilter
                key={dateFilterResetKey}
                setStartDate={(val) => {
                  setDate('start')(val);
                  setCheckDataFlag(true);
                }}
                setEndDate={(val) => {
                  setDate('stop')(val);
                  setCheckDataFlag(true);
                }}
                startDate={startDate}
                stopDate={stopDate}
                errorMsg={{
                  startDate:
                    errorMsg?.startDate ===
                    i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') })
                      ? i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') })
                      : null,
                  stopDate: errorMsg?.stopDate,
                }}
                startDatePickProps={{
                  disabled: false,
                  className: classNames(classes.datepicker),
                }}
                stopDatePickProps={{
                  disabled: false,
                  className: classNames(classes.datepicker),
                }}
              />
              {errorMsg?.startDate !==
                i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') }) &&
              errorMsg?.startDate ? (
                <Typography className={classes.errorMsg}>{errorMsg?.startDate ?? ''}</Typography>
              ) : (
                ''
              )}
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center">
              <div className={classes.filterStatusTitle}>{i18n.t('BEDSTATUS_STATUS')}: </div>
              <AutoCompleteMultiCheckbox
                id="bed_transferOut_status"
                itemList={transferInStatusList}
                placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
                value={_.filter(transferInStatus, (item) => defaultTransferInStatusValues.includes(item?.value))}
                onChange={(event) => setTransferInStatus(event)}
                className={classes.filterStatus}
                height="2.6rem"
                width="10.5rem"
              />
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center">
              <div className={classes.filterStatusTitle}>Admission Type:</div>
              <AutoCompleteMultiCheckbox
                itemList={admissionTypeList}
                value={admissionTypes}
                onChange={(value) => setTransferInFilter((prev) => ({ ...prev, admissionTypes: value }))}
                width="11rem"
                height="2.6rem"
                placeholder="Admission Types"
                freeSolo
                highlight="#FFFFCC"
                selectAll={false}
              />
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center">
              <div className={classes.filterStatusTitle}>Specialty:</div>
              <AutoCompleteMultiCheckbox
                itemList={specialtyList}
                value={specialtys}
                onChange={(value) => setTransferInFilter((prev) => ({ ...prev, specialtys: value }))}
                width="7rem"
                height="2.6rem"
                placeholder="Specialty"
                freeSolo
                highlight="#FFFFCC"
                selectAll={false}
              />
            </Box>
            <ButtonBase className={classes.reset} onClick={handleResetFilter}>
              Reset
            </ButtonBase>
            <Box className={classes.flexSpace} />
            <ButtonBase
              id="bed_excel_btn1"
              className={classes.newBooking}
              onClick={() => {
                if (!loginInfo.flag) {
                  setLoginPopup(true);
                  return;
                }
                exportPwdExcel(exportExcelHandle)();
              }}
            >
              <DocIcon className={classes.dateTimeIcon} />
              <Typography>{i18n.t('BEDSTATUS_EXPORT_EXCEL')}</Typography>
            </ButtonBase>
            <ButtonBase id="bed_new_btn1" className={classes.newBooking} onClick={handleClickNewRequest}>
              <DateTimeIcon className={classes.dateTimeIcon} />
              <Typography>{i18n.t('BEDSTATUS_NEW_REQUEST')}</Typography>
            </ButtonBase>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {detailOpen && (
              <Box display="flex" alignItems="center" marginTop="1rem">
                <Typography className={classes.ward}>{getWardName()}</Typography>
                <div style={{ flexDirection: 'column', display: 'flex' }}>
                  <div className={classes.columnBox}>
                    <Grid container flex="1">
                      {columnData.map((item, index) => {
                        return (
                          <ColumnItem
                            key={item.id}
                            {...item}
                            title={columnTitles[index]}
                            iconBgColor={iconBgColors[index]}
                            className={classes.columnItem}
                            xs={3}
                            sm={3}
                            md={3}
                          />
                        );
                      })}
                    </Grid>
                  </div>
                </div>
              </Box>
            )}
          </Box>
        </div>
        <div style={{ position: 'relative' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <ButtonBase className={classes.detailBtn} onClick={handleOpenDetail}>
              Statistic Detail
              {detailOpen ? <DropUpIcon className={classes.dropIcon} /> : <DropDownIcon className={classes.dropIcon} />}
            </ButtonBase>
          </Box>
        </div>
        <Box marginTop="1rem" flex={1} minHeight={0} display="flex" flexDirection="column">
          <TransferInTable
            data={transferInList}
            handleScroll={handleScroll}
            openEdit={(data) => {
              if (!loginInfo?.flag) {
                setLoginPopup(true);
                return;
              }
              setEditData(data);
              if (data?.type === 'IN') {
                setTransferInPopOpen(true);
              } else {
                setTransferOutPopOpen(true);
              }
            }}
            handleUpdateSingleData={handleUpdateSingleData}
            admissionTypeList={admissionTypeList}
          />
        </Box>
        {transferOutPopOpen && (
          <TransferOutPop
            open={transferOutPopOpen}
            closePop={() => closePop('TO')}
            title={i18n.t('EADMISSION_TRANSFER_IN')}
            allData={editData}
            saveHandle={handleTOSubmit}
            transferInPopupOptions={transferInPopupOptions}
            transferInSetting={eadViewVisible?.transferIn}
          />
        )}
        {transferInPopOpen && (
          <TransferInPop
            allData={editData}
            open={transferInPopOpen}
            closePop={() => closePop('TI')}
            title={i18n.t('BEDSTATUS_TRANSFER_IN_BUTTON')}
            saveHandle={handleEditTIRequest}
            transferInPopupOptions={transferInPopupOptions}
          />
        )}

        {loginPopup && <LoginPop closehandle={() => setLoginPopup(false)} submitHandle={() => setLoginPopup(false)} />}
      </div>
    </Box>
  );
};

export default TransferIn;
